import React from 'react';

import type { IconName } from 'shared';

import Icon from './Icon';
import Img, { Props as ImgProps } from './Img';
import Layout from './Layout';
import { OnMeasure } from './types';

interface Props extends Omit<ImgProps, 'width' | 'height' | 'srcKey'> {
  size: 'tiny' | 'mini' | 'smallThumbnail' | 'thumbnail' | 'sm' | 'lg' | 'hero';
  onMeasure?: OnMeasure;
  srcKey?: string | null;
  /** Shown when `srcKey` is missing. Defaults to `miscellaneous`. */
  placeholderIcon?: IconName;
}

const SIZE_CONFIG = {
  tiny: { width: 24, height: 24 },
  /** ~32px display at 2x DPR; use instead of tiny when CSS box is larger than 24px */
  mini: { width: 64, height: 64 },
  smallThumbnail: { width: 208, height: 208 },
  thumbnail: { width: 320, height: 320 },
  sm: { width: 600, height: 600 },
  lg: { width: 1200, height: 1200 },
  hero: { width: 3000, height: 1080 }
} as const;

const PLACEHOLDER_DEFAULT_SIZE = 36;

const ProductImg: React.FC<Props> = ({ size, srcKey, placeholderIcon = 'miscellaneous', ...props }) => {
  const pixelWidth =
    typeof props.style?.width === 'number'
      ? props.style?.width
      : parseInt(props.style?.width?.replace('px', '') || '0');
  const placeholderWidth = typeof props.style?.width === 'number' ? props.style.width : PLACEHOLDER_DEFAULT_SIZE;
  const placeholderHeight = typeof props.style?.height === 'number' ? props.style.height : PLACEHOLDER_DEFAULT_SIZE;

  return srcKey ? (
    <Img srcKey={srcKey} {...props} {...SIZE_CONFIG[size]} />
  ) : (
    <Layout
      color="gray-25"
      borderRadius={16}
      align="center"
      justify="center"
      flexShrink={0}
      style={{
        width: placeholderWidth,
        height: placeholderHeight,
        minWidth: placeholderWidth,
        minHeight: placeholderHeight,
        objectFit: 'cover',
        boxSizing: 'border-box',
        ...props.style
      }}
    >
      <Icon name={placeholderIcon} size={pixelWidth > 0 ? pixelWidth / 1.4 : 36} color="gray-200" />
    </Layout>
  );
};

export default ProductImg;
